import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  applyThemeTo,
  clearThemeFrom,
  mergeThemes,
  readStylesheetTheme,
} from '~/common/helpers';
import { ICustomerThemesForm } from '~/common/types';

export type ThemePreviewTarget = 'global' | number;

/**
 * Previews unsaved theme colors on the whole app. The colors go on <body>, not on :root where
 * applyTheme writes, so an application context refetch during the preview cannot undo it, and
 * stopping only has to clear <body> to fall back to the applied theme. An empty color is written
 * with its stylesheet default: left empty, <body> would inherit the applied theme from :root and
 * show a color that saving would not keep.
 */
export const useThemePreview = (themes: ICustomerThemesForm) => {
  const [previewing, setPreviewing] = useState<ThemePreviewTarget | null>(null);

  const previewedTheme = useMemo(() => {
    if (previewing === null) {
      return null;
    }
    if (previewing === 'global') {
      return themes;
    }
    const criteriaTheme = themes.criteriaThemes?.[previewing];

    return criteriaTheme ? mergeThemes(themes, criteriaTheme) : null;
  }, [previewing, themes]);

  useEffect(() => {
    if (!previewedTheme) {
      return;
    }
    applyThemeTo(document.body, previewedTheme, readStylesheetTheme());

    return () => clearThemeFrom(document.body);
  }, [previewedTheme]);

  const toggle = useCallback((target: ThemePreviewTarget) => {
    setPreviewing((current) => (current === target ? null : target));
  }, []);

  const stop = useCallback(() => setPreviewing(null), []);

  return { previewing, toggle, stop };
};
